import { useEffect, useState, useCallback } from 'react';
import {
  getAllTutorInvoices,
  openTutorInvoices as openTutorInvoicesRequest,
  udpateTutorInvoiceStatus,
} from '~common/api';

const isStagingFrontend = import.meta.env.VITE_SENTRY_ENV === 'staging';

export default () => {
  const [state, setState] = useState({
    invoices: [],
    loading: true,
    error: null,
    alert: null,
    selectionModel: [],
    selectedInvoice: null,
    showReviewInvoiceDailog: false,
    showOpenDraftInvoicesAll: false,
    showOpenDraftInvoicesDialog: false,
    openingDraftInvoices: false,
    openDraftInvoicesResult: null,
  });

  const handleGetTutorInvoices = useCallback(async () => {
    const result = await getAllTutorInvoices();

    setState((prevState) => ({
      ...prevState,
      loading: false,
      invoices: result?.ok?.invoices ?? [],
      showOpenDraftInvoicesAll: Boolean(result?.ok?.showOpenDraftInvoicesAll),
      error: result.isError
        ? 'There has been an error fetching tutor invoices please try again later.'
        : null,
    }));

    return result;
  }, []);

  const onSelectionModelChange = useCallback((selectionModel) => {
    const invoiceId = selectionModel[0];
    const selectedInvoice = state.invoices
      ? state.invoices?.find((invoice) => invoice.id === invoiceId)
      : null;

    setState((prevState) => ({
      ...prevState,
      selectionModel,
      selectedInvoice,
      error: null,
    }));
  }, [state.invoices]);

  const onRefreshClick = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      invoices: [],
      loading: true,
      error: null,
      alert: null,
      selectionModel: [],
      selectedInvoice: null,
    }));
    handleGetTutorInvoices();
  }, [handleGetTutorInvoices]);

  const onReviewInvoiceClick = useCallback(() => {
    if (state.selectedInvoice) {
      setState((prevState) => ({
        ...prevState,
        showReviewInvoiceDailog: true,
        error: null,
      }));
    }
  }, [state.selectedInvoice]);

  const onReviewInvoiceDailogClose = () => {
    setState((prevState) => ({
      ...prevState,
      showReviewInvoiceDailog: false,
    }));
  };

  const onReviewInvoiceDialogSubmit = useCallback(async (status, invoice) => {
    const result = await udpateTutorInvoiceStatus({
      invoiceId: invoice.id,
      tutorId: invoice.metadata.tutor.id,
      status,
    });

    setState((prevState) => ({
      ...prevState,
      ...(result.isOk && {
        invoices: prevState.invoices.map((inv) => (
          inv.id === invoice.id ? result.ok.result : inv
        )),
      }),
      error: result.isError
        ? `There has been an error updating tutor invoice ${invoice.id} for ${invoice.metadata.tutor.firstName} ${invoice.metadata.tutor.lastName}`
        : null,
      showReviewInvoiceDailog: !result.isError,
    }));
  }, [state]);

  const onOpenDraftInvoicesClick = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      showOpenDraftInvoicesDialog: true,
      openDraftInvoicesResult: null,
      error: null,
      alert: null,
    }));
  }, []);

  const onOpenDraftInvoicesDialogClose = useCallback(() => {
    if (state.openingDraftInvoices) {
      return;
    }

    setState((prevState) => ({
      ...prevState,
      showOpenDraftInvoicesDialog: false,
    }));
  }, [state.openingDraftInvoices]);

  const onOpenDraftInvoicesDialogSubmit = useCallback(async (dryRun) => {
    setState((prevState) => ({
      ...prevState,
      openingDraftInvoices: true,
      error: null,
      alert: null,
      openDraftInvoicesResult: null,
    }));

    const result = await openTutorInvoicesRequest({ dryRun });
    const summary = result?.ok;
    const successMessage = dryRun
      ? `Dry run complete: ${summary?.openedCount ?? 0} invoices would be opened and ${summary?.failedCount ?? 0} would fail.`
      : `Tutor draft invoices opened: ${summary?.openedCount ?? 0} opened and ${summary?.failedCount ?? 0} failed.`;

    setState((prevState) => ({
      ...prevState,
      openingDraftInvoices: false,
      openDraftInvoicesResult: summary ?? null,
      ...(result.isOk && {
        alert: {
          severity: summary?.failedCount > 0 ? 'warning' : 'success',
          message: successMessage,
        },
      }),
      error: result.isError
        ? 'There has been an error opening tutor draft invoices please try again later.'
        : null,
      showOpenDraftInvoicesDialog: result.isError || dryRun,
    }));

    if (result.isOk && !dryRun) {
      await handleGetTutorInvoices();
    }
  }, [handleGetTutorInvoices]);

  useEffect(() => {
    handleGetTutorInvoices();
  }, [handleGetTutorInvoices]);

  return {
    ...state,
    canOpenDraftInvoices: state.showOpenDraftInvoicesAll && isStagingFrontend,
    onSelectionModelChange,
    onRefreshClick,
    onReviewInvoiceClick,
    onReviewInvoiceDailogClose,
    onReviewInvoiceDialogSubmit,
    onOpenDraftInvoicesClick,
    onOpenDraftInvoicesDialogClose,
    onOpenDraftInvoicesDialogSubmit,
  };
};
